<template>
  <div class="mb-2">
    <label
        for="pos-print"
        class="form-label"
    >
      Pos Print Size(MM)
    </label>
    <input
        type="number"
        class="form-control width-20-per"
        id="pos-print"
        v-model="posPrintSize"
        @change="handlePosPrintSizeChange"
    >
  </div>

  <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
    <label
        class="form-check-label"
        for="is-voucher-approval-enabled"
    >
      Pos Bill Marketing Direct Incentive Allow
    </label>
    <input
        v-model="isPosBillMarketingIncentiveAllow"
        class="form-check-input m-0"
        type="checkbox"
        role="switch"
        id="is-voucher-approval-enabled"
        @click="handlePosBillMarketingIncentiveAllow"
    />
  </div>

  <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
    <label
        class="form-check-label"
        for="invoice-create-with-challan"
    >
      Auto Create Invoice With Challan
    </label>
    <input
        v-model="isInvoiceCreateWithChallan"
        class="form-check-input m-0"
        type="checkbox"
        role="switch"
        id="invoice-create-with-challan"
        @click="handleInvoiceCreateWithChallan"
    />
  </div>

  <hr class="my-2">

  <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-1">
    <label
        class="form-check-label"
        for="invoice-due-reminder"
    >
      Send Service Invoice Due Reminders
    </label>
    <input
        v-model="isInvoiceDueReminder"
        class="form-check-input m-0"
        type="checkbox"
        role="switch"
        id="invoice-due-reminder"
        @click="handleInvoiceDueReminder"
    />
  </div>
  <p class="small mb-2">
    Reminder days: a positive number sends before the due date, 0 on the due date and a negative number after the due date while a balance is still due. Reminders go out at the first scheduler run after the send time. SMS and email use the Recurring Invoice templates.
  </p>

  <div class="table-responsive">
    <table class="table table-bordered align-middle reminder-table">
      <thead>
      <tr>
        <th class="width-days">Reminder Days</th>
        <th>When</th>
        <th class="width-time">Send Time</th>
        <th class="text-center">SMS</th>
        <th class="text-center">Email</th>
        <th class="text-center">Active</th>
        <th class="text-center">Action</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(rule, index) in reminderRules" :key="rule.row_key">
        <td>
          <input v-model.number="rule.reminder_days" type="number" min="-365" max="365" class="form-control form-control-sm">
        </td>
        <td>{{ getReminderWhenLabel(rule.reminder_days) }}</td>
        <td>
          <input v-model="rule.send_time" type="time" class="form-control form-control-sm">
        </td>
        <td class="text-center">
          <input v-model="rule.is_sms" class="form-check-input" type="checkbox">
        </td>
        <td class="text-center">
          <input v-model="rule.is_email" class="form-check-input" type="checkbox">
        </td>
        <td class="text-center">
          <div class="form-check form-switch d-inline-block">
            <input v-model="rule.is_active" class="form-check-input" type="checkbox" role="switch">
          </div>
        </td>
        <td class="text-center">
          <button class="btn btn-sm btn-outline-danger" @click="removeReminderRule(index)">Delete</button>
        </td>
      </tr>
      </tbody>
    </table>
    <p v-if="! hasReminderRules" class="text-center text-muted my-2">No reminder added yet</p>
  </div>

  <div class="d-flex gap-1 mb-2">
    <button class="btn btn-sm btn-outline-primary" @click="addReminderRule">
      <i class="fas fa-plus"></i> Add Reminder
    </button>
    <button class="btn btn-sm btn-primary" @click="saveReminderRules">Save Reminders</button>
  </div>

    <GlobalLoader />
</template>

<script setup>
import {computed, inject, onMounted, ref} from "vue";
    import handleCompany from "@/services/modules/company";
    import GlobalLoader from "@/components/atom/GlobalLoader.vue";
    import {useStore} from "vuex";
    import {useRoute} from "vue-router";

    const posPrintSize = ref(null)
    const isPosBillMarketingIncentiveAllow = ref(false)
    const isInvoiceCreateWithChallan = ref(false)
    const isInvoiceDueReminder = ref(false)
    const reminderRules = ref([])
    const REMINDER_RULE_KEYS = ['reminder_days', 'send_time', 'is_sms', 'is_email', 'is_active']
    const REMINDER_MAX_DAYS = 365
    const REMINDER_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
    let reminderRowKey = 0
    const { getCompanyPreferences, modifyCompanyPreference } = handleCompany();
    const $store = useStore();
    const $route = useRoute();
    const showSuccess =  inject('showSuccess');
    const showError = inject('showError');
    const companyId = $route.params.companyId;

    const handlePosPrintSizeChange = (e) => {
        const nextValue = e.target.value;

        const data = {
            company_id: companyId,
            field: "pos_print_size",
            value: nextValue,
        };

        handleUpdate(data);
    }

    const handlePosBillMarketingIncentiveAllow = (e) => {
      const nextValue = e.target.checked;

      const data = {
        company_id: companyId,
        field: "pos_bill_marketing_incentive_allow",
        value: nextValue,
      };

      handleUpdate(data);
    }

    const handleInvoiceCreateWithChallan = (e) => {
      const nextValue = e.target.checked;

      const data = {
        company_id: companyId,
        field: "invoice_create_with_challan",
        value: nextValue,
      };

      handleUpdate(data);
    }

    const handleInvoiceDueReminder = (e) => {
      const data = {
        company_id: companyId,
        field: "invoice_due_reminder",
        value: e.target.checked,
      };

      handleUpdate(data);
    }

    const hasReminderRules = computed(() => reminderRules.value.length > 0)

    const nextReminderRowKey = () => {
      reminderRowKey += 1
      return 'rule-' + reminderRowKey
    }

    const getReminderWhenLabel = (days) => {
      const value = Number(days)
      if (days === '' || Number.isNaN(value)) return ''
      if (value === 0) return 'On due date'
      if (value > 0) return value + ' day(s) before due date'
      return Math.abs(value) + ' day(s) after due date'
    }

    const toReminderRow = (entry) => {
      return {
        row_key: nextReminderRowKey(),
        reminder_days: Number(entry.reminder_days),
        send_time: String(entry.send_time),
        is_sms: Number(entry.is_sms) === 1,
        is_email: Number(entry.is_email) === 1,
        is_active: Number(entry.is_active) === 1
      }
    }

    const toReminderEntry = (rule) => {
      return {
        reminder_days: Number(rule.reminder_days),
        send_time: rule.send_time,
        is_sms: rule.is_sms ? 1 : 0,
        is_email: rule.is_email ? 1 : 0,
        is_active: rule.is_active ? 1 : 0
      }
    }

    const isReminderEntry = (entry) => {
      if (! entry || typeof entry !== 'object' || Array.isArray(entry)) return false
      return REMINDER_RULE_KEYS.every(key => Object.prototype.hasOwnProperty.call(entry, key))
    }

    const parseReminderRules = (value) => {
      try {
        const entries = JSON.parse(value)
        if (! Array.isArray(entries)) return []
        return entries.filter(isReminderEntry).map(toReminderRow)
      } catch (err) {
        return []
      }
    }

    const getReminderRuleError = (rule) => {
      const days = Number(rule.reminder_days)
      if (rule.reminder_days === '' || ! Number.isInteger(days)) return 'Reminder days must be a whole number'
      if (Math.abs(days) > REMINDER_MAX_DAYS) return 'Reminder days must be between -365 and 365'
      if (! REMINDER_TIME_PATTERN.test(rule.send_time || '')) return 'Select the send time of every reminder'
      if (! rule.is_sms && ! rule.is_email) return 'Select SMS or Email for every reminder'
      return ''
    }

    const getReminderValidationError = () => {
      const ruleError = reminderRules.value.map(getReminderRuleError).find(error => error !== '')
      if (ruleError) return ruleError
      const days = reminderRules.value.map(rule => Number(rule.reminder_days))
      if (new Set(days).size !== days.length) return 'Each reminder day can be added only once'
      return ''
    }

    const addReminderRule = () => {
      reminderRules.value.push({
        row_key: nextReminderRowKey(),
        reminder_days: 0,
        send_time: '10:00',
        is_sms: false,
        is_email: true,
        is_active: true
      })
    }

    const removeReminderRule = (index) => {
      reminderRules.value.splice(index, 1)
    }

    const saveReminderRules = () => {
      const error = getReminderValidationError()
      if (error) {
        showError(error)
        return
      }

      const data = {
        company_id: companyId,
        field: "invoice_due_reminder_rules",
        value: JSON.stringify(reminderRules.value.map(toReminderEntry)),
      };

      handleUpdate(data);
    }

    const handleUpdate = (data) => {
        $store.state.loading = true;

        modifyCompanyPreference(data)
            .then((res) => {
                showSuccess(res.message);
            })
            .finally(() => ($store.state.loading = false));
    }

    const getInitialValue = (data, field) => {
        const item = data.find((item => item.field === field))

        return item ? item.value : null;
    }

    const parseBoolean = (v) => {
      if (typeof v === "boolean") return v;
      if (typeof v === "number") return v === 1;
      if (typeof v === "string") return v.trim() === "1";
      return false;
    }

    onMounted(() => {
        $store.state.loading = true

        const queryObject = {
            company_id: companyId,
            fields: 'pos_print_size,pos_bill_marketing_incentive_allow,invoice_create_with_challan,invoice_due_reminder,invoice_due_reminder_rules'
        }
        const query = '?' + new URLSearchParams(queryObject).toString();

        getCompanyPreferences(query)
            .then(res => {
              if (res.data){
                const data = res.data;
                posPrintSize.value = getInitialValue(data, 'pos_print_size')
                isPosBillMarketingIncentiveAllow.value = parseBoolean(getInitialValue(data, 'pos_bill_marketing_incentive_allow'))
                isInvoiceCreateWithChallan.value = parseBoolean(getInitialValue(data, 'invoice_create_with_challan'))
                isInvoiceDueReminder.value = parseBoolean(getInitialValue(data, 'invoice_due_reminder'))
                reminderRules.value = parseReminderRules(getInitialValue(data, 'invoice_due_reminder_rules'))
              }
            })
            .finally(() => $store.state.loading = false);
    });
</script>

<style scoped>
.reminder-table .width-days {
  width: 130px;
}
.reminder-table .width-time {
  width: 140px;
}
</style>
